import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Drawer from '../Drawer';
import { LoaderSpinner } from '../LoaderSpinner';
import { fmt$ } from '../../consts/demandplanning';

const PERIOD_VIEWS = {
  YTD: 'ytd',
  ACTUALS: 'actuals',
};

const METRIC_VIEWS = {
  REVENUE: 'revenue',
  UNITS: 'units',
};

const SALES_CHANNEL_VIEWS = {
  SHOPIFY: 'shopify',
  AMAZON: 'amazon',
};

const SALES_CHANNEL_LABELS = {
  [SALES_CHANNEL_VIEWS.SHOPIFY]: 'Shopify',
  [SALES_CHANNEL_VIEWS.AMAZON]: 'Amazon',
};

function formatBool(value) {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return '—';
}

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function fmtUnits(value) {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(value) || 0);
}

function fmtMetric(value, metric) {
  return metric === METRIC_VIEWS.UNITS ? fmtUnits(value) : fmt$(value);
}

function formatYtdPeriodLabel(planningYear, closedMonthCount, salesChannel) {
  const channelLabel = SALES_CHANNEL_LABELS[salesChannel] ?? 'Shopify';
  const year = Number(planningYear);
  const count = Number(closedMonthCount);
  if (!Number.isFinite(year) || !Number.isFinite(count) || count <= 0) {
    return `No closed months yet for ${planningYear ?? '—'}`;
  }
  if (count >= 12) {
    return `Full year ${year} (${channelLabel})`;
  }
  const endMonth = MONTH_LABELS[count - 1] ?? '';
  return `Jan–${endMonth} ${year} (${channelLabel}, closed months)`;
}

function formatActualsPeriodLabel(actualYear, salesChannel) {
  const channelLabel = SALES_CHANNEL_LABELS[salesChannel] ?? 'Shopify';
  const year = Number(actualYear);
  if (!Number.isFinite(year)) return 'Prior year —';
  return `Full year ${year} (${channelLabel})`;
}

function getConsoleBucket(item, periodView) {
  if (periodView === PERIOD_VIEWS.ACTUALS) {
    return item?.consoleCompare?.actuals ?? null;
  }
  return item?.consoleCompare?.ytd ?? null;
}

function getFdpTotal(item, periodView, metric) {
  if (metric === METRIC_VIEWS.UNITS) {
    return periodView === PERIOD_VIEWS.ACTUALS
      ? Number(item.actualsUnits ?? 0)
      : Number(item.ytdUnits ?? 0);
  }
  return periodView === PERIOD_VIEWS.ACTUALS
    ? Number(item.actualsRevenue ?? 0)
    : Number(item.ytdRevenue ?? 0);
}

function getFdpMonthly(item, periodView, metric) {
  if (metric === METRIC_VIEWS.UNITS) {
    return periodView === PERIOD_VIEWS.ACTUALS
      ? Array.isArray(item.actualsMonthlyUnits)
        ? item.actualsMonthlyUnits
        : []
      : Array.isArray(item.monthlyUnits)
        ? item.monthlyUnits
        : [];
  }
  return periodView === PERIOD_VIEWS.ACTUALS
    ? Array.isArray(item.actualsMonthlyRevenue)
      ? item.actualsMonthlyRevenue
      : []
    : Array.isArray(item.monthlyRevenue)
      ? item.monthlyRevenue
      : [];
}

function getConsoleTotal(consoleBucket, metric) {
  if (!consoleBucket) return 0;
  return metric === METRIC_VIEWS.UNITS
    ? Number(consoleBucket.units ?? 0)
    : Number(consoleBucket.grossSales ?? 0);
}

function getConsoleMonthly(consoleBucket, metric) {
  if (!consoleBucket) return [];
  return metric === METRIC_VIEWS.UNITS
    ? Array.isArray(consoleBucket.monthlyUnits)
      ? consoleBucket.monthlyUnits
      : []
    : Array.isArray(consoleBucket.monthlyGross)
      ? consoleBucket.monthlyGross
      : [];
}

function valuesMatch(fdpValue, excelValue, metric) {
  const a = Number(fdpValue) || 0;
  const b = Number(excelValue) || 0;
  const tolerance = metric === METRIC_VIEWS.UNITS ? 0.5 : 1;
  return Math.abs(a - b) <= tolerance;
}

function itemTotalMismatch(item, periodView, metric) {
  if (!item?.sku) return false;
  const consoleBucket = getConsoleBucket(item, periodView);
  const fdpTotal = getFdpTotal(item, periodView, metric);
  const excelTotal = getConsoleTotal(consoleBucket, metric);
  return !valuesMatch(fdpTotal, excelTotal, metric);
}

/** Summary compare: flag when totals differ or any included SKU mismatches (offsets can net to zero). */
function summaryCompareMismatch(aggregateFdp, aggregateExcel, skuMismatchCount, metric, showCompare) {
  if (!showCompare) return false;
  if (skuMismatchCount > 0) return true;
  return !valuesMatch(aggregateFdp, aggregateExcel, metric);
}

/** Matches sales section: omitted from category/YTD rollups when demand-excluded. */
function isIncludedInSalesTotals(item) {
  if (item?.includedInSalesTotals === true) return true;
  if (item?.includedInSalesTotals === false) return false;
  return item?.excludedFromForecastDemand !== true;
}

function MetricCell({ fdpValue, excelValue, metric, showCompare, muted, bold }) {
  const mismatch = showCompare && !valuesMatch(fdpValue, excelValue, metric);
  return (
    <td
      className={`whitespace-nowrap px-3 py-2 text-right tabular-nums ${
        bold ? 'font-medium' : ''
      } ${muted ? 'text-gray-500' : 'text-gray-700'}`}
    >
      <div>{fmtMetric(fdpValue, metric)}</div>
      {showCompare ? (
        <div className={`text-[10px] leading-tight ${mismatch ? 'font-medium text-red-600' : 'text-gray-500'}`}>
          Excel: {fmtMetric(excelValue, metric)}
        </div>
      ) : null}
    </td>
  );
}

export default function ForecastDemandProductsDialog({
  open,
  onOpenChange,
  demandPlanningId,
  demandPlanningYear,
  marginFromSidebar,
  drawerWidth,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [actualYear, setActualYear] = useState(null);
  const [ytdClosedMonthCount, setYtdClosedMonthCount] = useState(null);
  const [ytdMonthLabels, setYtdMonthLabels] = useState([]);
  const [actualMonthLabels, setActualMonthLabels] = useState([]);
  const [salesChannel, setSalesChannel] = useState(SALES_CHANNEL_VIEWS.SHOPIFY);
  const [periodView, setPeriodView] = useState(PERIOD_VIEWS.YTD);
  const [metricView, setMetricView] = useState(METRIC_VIEWS.REVENUE);
  const [showExcelCompare, setShowExcelCompare] = useState(true);
  const [showOnlyMismatch, setShowOnlyMismatch] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) {
      setSearch('');
      setShowOnlyMismatch(false);
      return;
    }
    if (!demandPlanningId || demandPlanningYear == null || demandPlanningYear === '') {
      setError('No forecast demand plan or year selected');
      setItems([]);
      setActualYear(null);
      setYtdClosedMonthCount(null);
      setYtdMonthLabels([]);
      setActualMonthLabels([]);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await axios.get('/api/v1/forecast-demand-products', {
          params: {
            demandPlanningId,
            demandPlanningYear,
            salesChannel,
          },
        });
        if (cancelled) return;
        setItems(Array.isArray(data?.items) ? data.items : []);
        setActualYear(data?.actualYear != null ? Number(data.actualYear) : null);
        setYtdClosedMonthCount(
          data?.ytdClosedMonthCount != null ? Number(data.ytdClosedMonthCount) : null
        );
        setYtdMonthLabels(Array.isArray(data?.ytdMonthLabels) ? data.ytdMonthLabels : []);
        setActualMonthLabels(Array.isArray(data?.actualMonthLabels) ? data.actualMonthLabels : []);
      } catch (err) {
        if (cancelled) return;
        const msg =
          typeof err?.response?.data === 'string'
            ? err.response.data
            : err?.response?.data?.error || err?.message || 'Failed to load forecast demand products';
        setError(msg);
        setItems([]);
        setActualYear(null);
        setYtdClosedMonthCount(null);
        setYtdMonthLabels([]);
        setActualMonthLabels([]);
        toast.error(msg, { position: 'bottom-right' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, demandPlanningId, demandPlanningYear, salesChannel]);

  const searchLower = search.trim().toLowerCase();
  const filteredItems = useMemo(() => {
    if (!searchLower) return items;
    return items.filter((item) => {
      const haystack = [
        item.sku,
        item.name,
        item.status,
        item.productGroupName,
        item.categoryName,
        item.subcategoryName,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(searchLower);
    });
  }, [items, searchLower]);

  const tableItems = useMemo(() => {
    if (!showOnlyMismatch || !showExcelCompare) return filteredItems;
    return filteredItems.filter((item) => itemTotalMismatch(item, periodView, metricView));
  }, [filteredItems, showOnlyMismatch, showExcelCompare, periodView, metricView]);

  const isActualsView = periodView === PERIOD_VIEWS.ACTUALS;
  const isUnitsMetric = metricView === METRIC_VIEWS.UNITS;
  const isAmazonChannel = salesChannel === SALES_CHANNEL_VIEWS.AMAZON;
  const channelLabel = SALES_CHANNEL_LABELS[salesChannel] ?? 'Shopify';
  const primarySourceLabel = isAmazonChannel ? 'live' : 'FDP';

  const monthLabels = useMemo(() => {
    if (isActualsView) {
      if (actualMonthLabels.length > 0) return actualMonthLabels;
      return MONTH_LABELS;
    }
    if (ytdMonthLabels.length > 0) return ytdMonthLabels;
    const count = Number(ytdClosedMonthCount);
    if (!Number.isFinite(count) || count <= 0) return [];
    return MONTH_LABELS.slice(0, count);
  }, [isActualsView, actualMonthLabels, ytdMonthLabels, ytdClosedMonthCount]);

  const periodLabel = isActualsView
    ? formatActualsPeriodLabel(actualYear ?? Number(demandPlanningYear) - 1, salesChannel)
    : formatYtdPeriodLabel(demandPlanningYear, ytdClosedMonthCount, salesChannel);

  const metricLabel = isUnitsMetric ? 'Units' : 'Gross sales';
  const totalColumnLabel = isActualsView
    ? isUnitsMetric
      ? 'LTM units'
      : 'LTM gross'
    : isUnitsMetric
      ? 'YTD units'
      : 'YTD gross';
  const totalSummaryLabel = isActualsView
    ? isUnitsMetric
      ? 'Total LTM units'
      : 'Total LTM gross'
    : isUnitsMetric
      ? 'Total YTD units'
      : 'Total YTD gross';
  const categorySummaryLabel = isActualsView
    ? isUnitsMetric
      ? 'LTM units by category'
      : 'LTM gross by category'
    : isUnitsMetric
      ? 'YTD units by category'
      : 'YTD gross by category';

  const totalsEligibleItems = useMemo(
    () => filteredItems.filter((item) => isIncludedInSalesTotals(item)),
    [filteredItems]
  );

  const demandExcludedCount = filteredItems.length - totalsEligibleItems.length;

  const {
    totalValue,
    monthlyTotals,
    consoleTotal,
    consoleMonthlyTotals,
    monthlySkuMismatchFlags,
    categoryTotals,
    compareStats,
  } =
    useMemo(() => {
      const byCategory = new Map();
      const monthCount = monthLabels.length;
      const monthSums = Array(monthCount).fill(0);
      const consoleMonthSums = Array(monthCount).fill(0);
      const monthlySkuMismatchFlags = Array(monthCount).fill(false);
      let total = 0;
      let consoleTotalSum = 0;
      let compareChecked = 0;
      let compareMatched = 0;
      let compareMismatched = 0;

      for (const item of totalsEligibleItems) {
        const consoleBucket = getConsoleBucket(item, periodView);
        const fdpTotal = getFdpTotal(item, periodView, metricView);
        const excelTotal = getConsoleTotal(consoleBucket, metricView);
        if (Number.isFinite(fdpTotal)) total += fdpTotal;
        if (Number.isFinite(excelTotal)) consoleTotalSum += excelTotal;

        if (showExcelCompare && item.sku) {
          compareChecked += 1;
          if (valuesMatch(fdpTotal, excelTotal, metricView)) {
            compareMatched += 1;
          } else {
            compareMismatched += 1;
          }
        }

        const fdpMonthly = getFdpMonthly(item, periodView, metricView);
        const excelMonthly = getConsoleMonthly(consoleBucket, metricView);
        for (let i = 0; i < monthCount; i += 1) {
          const monthVal = Number(fdpMonthly[i] ?? 0);
          const excelMonthVal = Number(excelMonthly[i] ?? 0);
          if (Number.isFinite(monthVal)) monthSums[i] += monthVal;
          if (Number.isFinite(excelMonthVal)) consoleMonthSums[i] += excelMonthVal;
          if (showExcelCompare && item.sku && !valuesMatch(monthVal, excelMonthVal, metricView)) {
            monthlySkuMismatchFlags[i] = true;
          }
        }

        const category = String(item.categoryName || '').trim() || 'Uncategorized';
        const existing = byCategory.get(category) || { value: 0, productCount: 0 };
        byCategory.set(category, {
          value: existing.value + (Number.isFinite(fdpTotal) ? fdpTotal : 0),
          productCount: existing.productCount + 1,
        });
      }

      const categories = [...byCategory.entries()]
        .map(([categoryName, stats]) => ({
          categoryName,
          value: Number(
            (isUnitsMetric ? stats.value : stats.value).toFixed(isUnitsMetric ? 0 : 2)
          ),
          productCount: stats.productCount,
        }))
        .sort((a, b) => b.value - a.value || a.categoryName.localeCompare(b.categoryName));

      return {
        totalValue: Number(total.toFixed(isUnitsMetric ? 0 : 2)),
        monthlyTotals: monthSums.map((value) =>
          Number(value.toFixed(isUnitsMetric ? 0 : 2))
        ),
        consoleTotal: Number(consoleTotalSum.toFixed(isUnitsMetric ? 0 : 2)),
        consoleMonthlyTotals: consoleMonthSums.map((value) =>
          Number(value.toFixed(isUnitsMetric ? 0 : 2))
        ),
        monthlySkuMismatchFlags,
        categoryTotals: categories,
        compareStats: { compareChecked, compareMatched, compareMismatched },
      };
    }, [
      totalsEligibleItems,
      monthLabels.length,
      periodView,
      metricView,
      showExcelCompare,
      isUnitsMetric,
    ]);

  return (
    <Drawer
      isOpen={open}
      onClose={() => onOpenChange?.(false)}
      showActionButtons={false}
      marginFromSidebar={marginFromSidebar}
      width={drawerWidth}
      bodyClassName="!p-0 flex flex-col min-h-0"
      headerContent={
        <div>
          <h2 className="text-xl font-bold text-gray-900">Forecast demand products</h2>
          <p className="mt-1 text-sm text-gray-500">
            Snapshot rows from <span className="font-mono text-xs">forecast_demand_products</span> for
            planning year {demandPlanningYear ?? '—'}.
          </p>
          <p className="mt-0.5 text-xs text-gray-500">
            {metricLabel} (direct {channelLabel} line): {periodLabel}
          </p>
          <p className="mt-0.5 text-xs text-gray-500">
            {isAmazonChannel
              ? 'Product list from forecast_demand_products; sales from live shopify_amazon_sales (Amazon channel).'
              : 'Sales from forecast_demand_products snapshot (Shopify channel only).'}
          </p>
          <p className="mt-0.5 text-xs text-gray-500">
            Summary totals exclude products with <span className="font-medium">Excluded (demand)</span> — same
            as the sales section rollups.
          </p>
          {showExcelCompare ? (
            <p className="mt-0.5 text-xs text-gray-500">
              Excel compare: {channelLabel}{' '}
              <span className="font-mono text-[11px]">
                {isAmazonChannel ? 'consoleSalesAmazonData' : 'consoleSalesData'}
              </span>{' '}
              by <span className="font-medium">Supply_SKU</span>.
            </p>
          ) : null}
        </div>
      }
    >
      <div className="flex shrink-0 flex-col gap-3 border-b border-gray-100 px-5 py-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Channel</span>
            <div className="inline-flex rounded-md border border-gray-300 bg-white p-0.5">
              <button
                type="button"
                onClick={() => setSalesChannel(SALES_CHANNEL_VIEWS.SHOPIFY)}
                className={`rounded px-3 py-1 text-xs font-semibold transition-colors ${
                  !isAmazonChannel ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                Shopify
              </button>
              <button
                type="button"
                onClick={() => setSalesChannel(SALES_CHANNEL_VIEWS.AMAZON)}
                className={`rounded px-3 py-1 text-xs font-semibold transition-colors ${
                  isAmazonChannel ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                Amazon
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Period</span>
            <div className="inline-flex rounded-md border border-gray-300 bg-white p-0.5">
              <button
                type="button"
                onClick={() => setPeriodView(PERIOD_VIEWS.YTD)}
                className={`rounded px-3 py-1 text-xs font-semibold transition-colors ${
                  !isActualsView ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                YTD {demandPlanningYear ?? ''}
              </button>
              <button
                type="button"
                onClick={() => setPeriodView(PERIOD_VIEWS.ACTUALS)}
                className={`rounded px-3 py-1 text-xs font-semibold transition-colors ${
                  isActualsView ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                Actuals {actualYear ?? (Number(demandPlanningYear) - 1 || '')}
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Metric</span>
            <div className="inline-flex rounded-md border border-gray-300 bg-white p-0.5">
              <button
                type="button"
                onClick={() => setMetricView(METRIC_VIEWS.REVENUE)}
                className={`rounded px-3 py-1 text-xs font-semibold transition-colors ${
                  !isUnitsMetric ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                Gross sales
              </button>
              <button
                type="button"
                onClick={() => setMetricView(METRIC_VIEWS.UNITS)}
                className={`rounded px-3 py-1 text-xs font-semibold transition-colors ${
                  isUnitsMetric ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                Units
              </button>
            </div>
          </div>
          <label className="flex cursor-pointer items-center gap-2 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={showExcelCompare}
              onChange={(event) => {
                const checked = event.target.checked;
                setShowExcelCompare(checked);
                if (!checked) setShowOnlyMismatch(false);
              }}
              className="rounded border-gray-300"
            />
            Compare to Excel
          </label>
          <label
            className={`flex items-center gap-2 text-xs ${
              showExcelCompare ? 'cursor-pointer text-gray-600' : 'cursor-not-allowed text-gray-400'
            }`}
          >
            <input
              type="checkbox"
              checked={showOnlyMismatch}
              disabled={!showExcelCompare}
              onChange={(event) => setShowOnlyMismatch(event.target.checked)}
              className="rounded border-gray-300 disabled:cursor-not-allowed"
            />
            Only mismatches
          </label>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search SKU, name, category…"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-gray-400 focus:outline-none"
          />
          <span className="shrink-0 text-sm text-gray-500">
            {tableItems.length}
            {showOnlyMismatch || searchLower ? ` / ${filteredItems.length}` : ''}
            {searchLower ? ` / ${items.length}` : ''} product(s)
          </span>
        </div>
      </div>

      {!loading && !error && filteredItems.length > 0 ? (
        <div className="shrink-0 border-b border-gray-100 px-5 py-4">
          <div className="mb-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {totalSummaryLabel} ({primarySourceLabel})
                </p>
                <p className="text-lg font-semibold tabular-nums text-gray-900">
                  {fmtMetric(totalValue, metricView)}
                </p>
                {showExcelCompare ? (
                  <p
                    className={`mt-0.5 text-xs tabular-nums ${
                      summaryCompareMismatch(
                        totalValue,
                        consoleTotal,
                        compareStats.compareMismatched,
                        metricView,
                        showExcelCompare
                      )
                        ? 'font-medium text-red-600'
                        : 'text-gray-500'
                    }`}
                  >
                    Excel: {fmtMetric(consoleTotal, metricView)}
                  </p>
                ) : null}
              </div>
              <div className="text-right text-xs text-gray-500">
                <p>
                  {totalsEligibleItems.length} in totals
                  {demandExcludedCount > 0 ? ` · ${demandExcludedCount} demand-excluded` : ''}
                  {searchLower ? ' (filtered)' : ''}
                </p>
                {showExcelCompare && compareStats.compareChecked > 0 ? (
                  <p className="mt-1">
                    {compareStats.compareMatched} match ·{' '}
                    <span
                      className={
                        compareStats.compareMismatched > 0 ? 'font-medium text-red-600' : undefined
                      }
                    >
                      {compareStats.compareMismatched} mismatch
                    </span>
                  </p>
                ) : null}
              </div>
            </div>
            {monthLabels.length > 0 ? (
              <div className="mt-3 flex flex-wrap gap-2 border-t border-gray-200 pt-3">
                {monthLabels.map((label, index) => (
                  <div
                    key={label}
                    className="min-w-[5.5rem] rounded-md border border-gray-200 bg-white px-2.5 py-1.5"
                  >
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{label}</p>
                    <p className="text-sm font-semibold tabular-nums text-gray-900">
                      {fmtMetric(monthlyTotals[index] ?? 0, metricView)}
                    </p>
                    {showExcelCompare ? (
                      <p
                        className={`text-[10px] tabular-nums ${
                          summaryCompareMismatch(
                            monthlyTotals[index] ?? 0,
                            consoleMonthlyTotals[index] ?? 0,
                            monthlySkuMismatchFlags[index] ? 1 : 0,
                            metricView,
                            showExcelCompare
                          )
                            ? 'font-medium text-red-600'
                            : 'text-gray-500'
                        }`}
                      >
                        Excel: {fmtMetric(consoleMonthlyTotals[index] ?? 0, metricView)}
                      </p>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              {categorySummaryLabel}
            </p>
            <div className="max-h-40 space-y-1.5 overflow-y-auto rounded-lg border border-gray-200 bg-white">
              {categoryTotals.map((row) => (
                <div
                  key={row.categoryName}
                  className="flex items-center justify-between gap-3 border-b border-gray-100 px-3 py-2 last:border-b-0"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-gray-900">{row.categoryName}</p>
                    <p className="text-xs text-gray-500">
                      {row.productCount} product{row.productCount === 1 ? '' : 's'}
                    </p>
                  </div>
                  <p className="shrink-0 text-sm font-semibold tabular-nums text-gray-900">
                    {fmtMetric(row.value, metricView)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      <div className="relative min-h-0 flex-1 overflow-y-auto px-5 py-4">
        {loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70">
            <LoaderSpinner width="40" height="40" border="3" colorSpinner="black" />
          </div>
        )}
        {error && !loading ? <p className="text-sm text-red-600">{error}</p> : null}
        {!loading && !error && filteredItems.length === 0 ? (
          <p className="text-sm text-gray-500">No products found for this planning year.</p>
        ) : null}
        {!loading && !error && filteredItems.length > 0 && tableItems.length === 0 ? (
          <p className="text-sm text-gray-500">No mismatches for the current period and metric.</p>
        ) : null}
        {!error && tableItems.length > 0 ? (
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
              <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-3 py-2">SKU</th>
                  <th className="px-3 py-2">Name</th>
                  {monthLabels.map((label) => (
                    <th key={label} className="whitespace-nowrap px-3 py-2 text-right">
                      {label}
                    </th>
                  ))}
                  <th className="px-3 py-2 text-right">{totalColumnLabel}</th>
                  <th className="px-3 py-2">Category</th>
                  <th className="px-3 py-2">Subcategory</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Forecastable</th>
                  <th className="px-3 py-2">Excluded (forecast)</th>
                  <th className="px-3 py-2">Excluded (demand)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white text-gray-900">
                {tableItems.map((item) => {
                  const consoleBucket = getConsoleBucket(item, periodView);
                  const fdpMonthly = getFdpMonthly(item, periodView, metricView);
                  const excelMonthly = getConsoleMonthly(consoleBucket, metricView);
                  const fdpTotal = getFdpTotal(item, periodView, metricView);
                  const excelTotal = getConsoleTotal(consoleBucket, metricView);
                  const inTotals = isIncludedInSalesTotals(item);
                  const rowMismatch = showExcelCompare && itemTotalMismatch(item, periodView, metricView);
                  return (
                    <tr
                      key={item.forecastDemandProductId ?? item.productId}
                      className={`hover:bg-gray-50 ${inTotals ? '' : 'bg-amber-50/40'} ${
                        rowMismatch ? 'bg-red-50/30' : ''
                      }`}
                    >
                      <td className="whitespace-nowrap px-3 py-2 font-medium">{item.sku || '—'}</td>
                      <td className="max-w-[12rem] truncate px-3 py-2" title={item.name || undefined}>
                        {item.name || '—'}
                      </td>
                      {monthLabels.map((label, index) => (
                        <MetricCell
                          key={`${item.forecastDemandProductId ?? item.productId}-${label}`}
                          fdpValue={fdpMonthly[index] ?? 0}
                          excelValue={excelMonthly[index] ?? 0}
                          metric={metricView}
                          showCompare={showExcelCompare}
                        />
                      ))}
                      <MetricCell
                        fdpValue={fdpTotal}
                        excelValue={excelTotal}
                        metric={metricView}
                        showCompare={showExcelCompare}
                        muted={!inTotals}
                        bold
                      />
                      <td className="px-3 py-2">{item.categoryName || '—'}</td>
                      <td className="px-3 py-2">{item.subcategoryName || '—'}</td>
                      <td className="px-3 py-2">{item.status || '—'}</td>
                      <td className="px-3 py-2">{formatBool(item.forecastable)}</td>
                      <td className="px-3 py-2">{formatBool(item.excludedFromForecast)}</td>
                      <td className="px-3 py-2">{formatBool(item.excludedFromForecastDemand)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </Drawer>
  );
}
